import { call, put, takeLatest } from 'redux-saga/effects';
import i18n from '../../../i18n';
import officerGroupService from '../../../services/officerGroupService';
import {
  fetchOfficerGroups,
  fetchOfficerGroupsSuccess,
  fetchOfficerGroupsFailure,
  fetchOfficerGroupById,
  fetchOfficerGroupByIdSuccess,
  fetchOfficerGroupByIdFailure,
  addOfficerGroup,
  addOfficerGroupSuccess,
  addOfficerGroupFailure,
  updateOfficerGroup,
  updateOfficerGroupSuccess,
  updateOfficerGroupFailure,
  deleteOfficerGroup,
  deleteOfficerGroupSuccess,
  deleteOfficerGroupFailure,
  bulkDeleteOfficerGroups,
  bulkDeleteOfficerGroupsSuccess,
  bulkDeleteOfficerGroupsFailure,
} from './slice';

/**
 * Translate error message from backend
 */
function translateError(errorMessage) {
  if (!errorMessage) return i18n.t('officerGroups.errors.unknown');

  const errorMap = {
    'Officer group name already exists': 'officerGroups.errors.nameExists',
    'Officer group not found': 'officerGroups.errors.officerGroupNotFound',
    'Failed to add officer group': 'officerGroups.errors.addFailed',
    'Failed to update officer group': 'officerGroups.errors.updateFailed',
    'Failed to delete officer group': 'officerGroups.errors.deleteFailed',
    'Failed to fetch officer groups': 'officerGroups.errors.fetchFailed',
    'Cannot delete officer group that is being used by positions': 'officerGroups.errors.cannotDeleteInUse',
  };

  for (const [key, translationKey] of Object.entries(errorMap)) {
    if (errorMessage.includes(key)) {
      return i18n.t(translationKey);
    }
  }

  return errorMessage;
}

function* handleFetchOfficerGroups(action) {
  try {
    const response = yield call(officerGroupService.getOfficerGroups, action.payload);
    yield put(fetchOfficerGroupsSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchOfficerGroupsFailure(translatedError));
  }
}

function* handleFetchOfficerGroupById(action) {
  try {
    const response = yield call(officerGroupService.getOfficerGroupById, action.payload);
    yield put(fetchOfficerGroupByIdSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchOfficerGroupByIdFailure(translatedError));
  }
}

function* handleAddOfficerGroup(action) {
  try {
    const response = yield call(officerGroupService.createOfficerGroup, action.payload);
    yield put(addOfficerGroupSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(addOfficerGroupFailure(translatedError));
  }
}

function* handleUpdateOfficerGroup(action) {
  try {
    const response = yield call(officerGroupService.updateOfficerGroup, action.payload);
    yield put(updateOfficerGroupSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(updateOfficerGroupFailure(translatedError));
  }
}

function* handleDeleteOfficerGroup(action) {
  try {
    const officerGroupId = action.payload;
    yield call(officerGroupService.deleteOfficerGroup, officerGroupId);
    yield put(deleteOfficerGroupSuccess(officerGroupId));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(deleteOfficerGroupFailure(translatedError));
  }
}

function* handleBulkDeleteOfficerGroups(action) {
  try {
    const officerGroupIds = action.payload;
    yield call(officerGroupService.bulkDeleteOfficerGroups, officerGroupIds);
    yield put(bulkDeleteOfficerGroupsSuccess(officerGroupIds));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(bulkDeleteOfficerGroupsFailure(translatedError));
  }
}

export function* watchOfficerGroupsActions() {
  yield takeLatest(fetchOfficerGroups.type, handleFetchOfficerGroups);
  yield takeLatest(fetchOfficerGroupById.type, handleFetchOfficerGroupById);
  yield takeLatest(addOfficerGroup.type, handleAddOfficerGroup);
  yield takeLatest(updateOfficerGroup.type, handleUpdateOfficerGroup);
  yield takeLatest(deleteOfficerGroup.type, handleDeleteOfficerGroup);
  yield takeLatest(bulkDeleteOfficerGroups.type, handleBulkDeleteOfficerGroups);
}

